/**
 * Every permission slug of the backend catalogue (backend/src/rbac/catalog.ts;
 * "no new permission slugs" this round, so the list matches today's
 * utils/default-permissions.ts). Kept in catalogue order: module by module.
 *
 * rbac/__tests__/slugs.test.ts and the backend catalog-consistency test keep
 * this list equal to the catalogue. Typing `can()` with PermissionSlug turns a
 * typo into a compile error instead of a silently hidden button.
 */
export const PERMISSION_SLUGS = [
  'dashboard.view',
  'advanced-dashboard.view',
  'notifications.view',
  'company-info.view',
  'projects.view', 'projects.create', 'projects.edit', 'projects.delete',
  'tasks.view', 'tasks.create', 'tasks.edit', 'tasks.delete',
  'employees.view', 'employees.create', 'employees.edit', 'employees.delete',
  'attendance.view', 'attendance.create', 'attendance.edit', 'attendance.approve',
  'leaves.view', 'leaves.create', 'leaves.approve', 'leaves.delete',
  'shifts.view', 'shifts.create', 'shifts.edit', 'shifts.delete',
  'departments.view', 'departments.create', 'departments.edit', 'departments.delete',
  'designations.view', 'designations.create', 'designations.edit', 'designations.delete',
  'holidays.view', 'holidays.create', 'holidays.edit', 'holidays.delete',
  'invoices.view', 'invoices.create', 'invoices.edit', 'invoices.delete',
  'expenses.view', 'expenses.create', 'expenses.approve', 'expenses.delete',
  'estimates.view', 'estimates.create', 'estimates.edit', 'estimates.delete',
  'proposals.view', 'proposals.create', 'proposals.edit', 'proposals.delete',
  'payments.view', 'payments.create', 'payments.edit', 'payments.delete',
  'bank-accounts.view', 'bank-accounts.create', 'bank-accounts.edit', 'bank-accounts.delete',
  'tickets.view', 'tickets.create', 'tickets.edit', 'tickets.delete',
  'calendar.view', 'calendar.create', 'calendar.edit', 'calendar.delete',
  'messages.view', 'messages.create',
  'reports.view', 'reports.export',
  'settings.view', 'settings.edit', 'settings.roles',
  'clients.view', 'clients.create', 'clients.edit', 'clients.delete',
  'contracts.view', 'contracts.create', 'contracts.edit', 'contracts.delete',
  'notices.view', 'notices.create', 'notices.edit', 'notices.delete',
  'assets.view', 'assets.create', 'assets.edit', 'assets.delete',
  'knowledge-base.view', 'knowledge-base.create', 'knowledge-base.edit', 'knowledge-base.delete',
  'appreciations.view', 'appreciations.create', 'appreciations.delete',
  'leads.view', 'leads.create', 'leads.edit', 'leads.delete',
  'tdrive.view', 'tdrive.upload', 'tdrive.delete',
  'payroll.view', 'payroll.create', 'payroll.edit', 'payroll.delete', 'payroll.approve', 'payroll.export',
  'qr-generator.view',
  'support-tickets.view', 'support-tickets.create',
  'ai-chatbot.view', 'ai-chatbot.create',
] as const;

export type PermissionSlug = (typeof PERMISSION_SLUGS)[number];

export type PermissionAction = 'view' | 'create' | 'edit' | 'delete' | 'approve' | 'upload' | 'export' | 'roles';

const SLUG_SET: ReadonlySet<string> = new Set(PERMISSION_SLUGS);

export function isPermissionSlug(value: unknown): value is PermissionSlug {
  return typeof value === 'string' && SLUG_SET.has(value);
}

/** 'knowledge-base.view' → 'knowledge-base'. */
export function moduleOf(slug: PermissionSlug): string {
  return slug.slice(0, slug.lastIndexOf('.'));
}

/** 'knowledge-base.view' → 'view'. */
export function actionOf(slug: PermissionSlug): PermissionAction {
  return slug.slice(slug.lastIndexOf('.') + 1) as PermissionAction;
}

/** Permission modules in catalogue order (the role editor's row order). */
export const PERMISSION_MODULES: readonly string[] = [...new Set(PERMISSION_SLUGS.map(moduleOf))];
